import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getPublicConfigs } from '@/api/config';

// 全站公开配置（site_name / page_size 等 is_public=1 的键）
// 登录页即可加载（接口无需鉴权）；系统配置页保存后通过 applyPublic 立即同步
export const useConfigStore = defineStore('config', () => {
    const siteName = ref('优臣GEO优化系统');
    const pageSize = ref(20);
    // AI 生成默认参数（系统配置页可改）
    const aiTemperature = ref(0.8);
    const aiMaxTokens = ref(4096);
    const aiKnowledgeRefs = ref(5);
    // 演示模式（登录页预填并展示演示账号）
    const demoMode = ref(false);
    const demoDisplayType = ref('credential');
    const demoAccount = ref('');
    const demoPassword = ref('');
    const demoSlogan = ref('');
    // 上传限制（MB）
    const avatarMaxMb = ref(5);
    const logoMaxMb = ref(2);
    // 监测断档提醒阈值（天）
    const staleDays = ref(3);
    // 计费模式总开关（false=畅用模式：全站隐藏余额入口）
    const billingEnabled = ref(true);
    // 官网联系方式（首页「加入我们」与全局页脚展示）
    const siteContactEmail = ref('biz@youchen-geo.com');
    const siteContactPhone = ref('400-000-0000');
    // 管理端顶部滚动公告（空字符串 = 不显示）
    const siteNotice = ref('');
    // 登录页品牌区文案
    const loginPage = ref({
        brandName: '优臣科技',
        title1: '生成式引擎优化',
        title2: 'GEO 管理系统',
        subtitle: 'AI 内容生成 · 多平台发布 · 关键词监控 · 数据分析\n一站式的生成式搜索引擎优化作战平台',
        footerText: 'YOUCHEN TECHNOLOGY',
        copyright: '优臣科技 © 2026 · GEO Optimization System',
        logoUrl: ''
    });
    const loaded = ref(false);

    function applyPublic(data) {
        if (!data) return;
        if (data.site_name) siteName.value = data.site_name;
        if (data.page_size) pageSize.value = Number(data.page_size) || 20;
        if (data.ai_default_temperature !== undefined) aiTemperature.value = Number(data.ai_default_temperature) || 0.8;
        if (data.ai_default_max_tokens) aiMaxTokens.value = Number(data.ai_default_max_tokens) || 4096;
        if (data.ai_knowledge_refs) aiKnowledgeRefs.value = Number(data.ai_knowledge_refs) || 5;
        if (data.demo_mode !== undefined) demoMode.value = data.demo_mode === true || data.demo_mode === 'true';
        if (data.demo_display_type !== undefined) demoDisplayType.value = data.demo_display_type || 'credential';
        if (data.demo_account !== undefined) demoAccount.value = data.demo_account || '';
        if (data.demo_password !== undefined) demoPassword.value = data.demo_password || '';
        if (data.demo_slogan !== undefined) demoSlogan.value = data.demo_slogan || '';
        if (data.upload_avatar_max_mb) avatarMaxMb.value = Number(data.upload_avatar_max_mb) || 5;
        if (data.upload_logo_max_mb) logoMaxMb.value = Number(data.upload_logo_max_mb) || 2;
        if (data.monitor_stale_days !== undefined) staleDays.value = Math.min(Math.max(Number(data.monitor_stale_days) || 3, 1), 14);
        if (data.billing_enabled !== undefined) billingEnabled.value = data.billing_enabled === true || data.billing_enabled === 'true';
        if (data.site_contact_email !== undefined) siteContactEmail.value = data.site_contact_email || siteContactEmail.value;
        if (data.site_contact_phone !== undefined) siteContactPhone.value = data.site_contact_phone || siteContactPhone.value;
        if (data.site_notice !== undefined) siteNotice.value = data.site_notice || '';
        const lp = loginPage.value;
        if (data.login_brand_name !== undefined) lp.brandName = data.login_brand_name || lp.brandName;
        if (data.login_title_1 !== undefined) lp.title1 = data.login_title_1 || lp.title1;
        if (data.login_title_2 !== undefined) lp.title2 = data.login_title_2 || lp.title2;
        if (data.login_subtitle !== undefined) lp.subtitle = data.login_subtitle || lp.subtitle;
        if (data.login_footer_text !== undefined) lp.footerText = data.login_footer_text || lp.footerText;
        if (data.login_copyright !== undefined) lp.copyright = data.login_copyright || lp.copyright;
        if (data.login_logo_url !== undefined) lp.logoUrl = data.login_logo_url || '';
        document.title = siteName.value;
    }

    async function load(force = false) {
        if (loaded.value && !force) return;
        try {
            const data = await getPublicConfigs();
            applyPublic(data);
            loaded.value = true;
        } catch {
            // 公共配置加载失败不阻断页面
        }
    }

    return { siteName, pageSize, aiTemperature, aiMaxTokens, aiKnowledgeRefs, demoMode, demoDisplayType, demoAccount, demoPassword, demoSlogan, avatarMaxMb, logoMaxMb, staleDays, billingEnabled, siteContactEmail, siteContactPhone, siteNotice, loginPage, loaded, load, applyPublic };
});
